import { noop } from "lodash-es";
//#region src/errors/abort_error.ts
/** this detects both our AbortError defined bellow and an abort error thrown by node when calling `fetch` */
function isAbortError(object) {
	return object instanceof Error && object.name === "AbortError";
}
var AbortError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "AbortError";
	}
};
//#endregion
//#region src/retry.ts
const isNotAbort = (err) => !isAbortError(err);
const shouldRetry = (check, error) => {
	return (Array.isArray(check) ? check : [check]).every((ch) => ch(error));
};
const waitMs = (msToWait) => new Promise((resolve) => {
	setTimeout(resolve, msToWait);
});
const FIRST_ATTEMPT = 1;
const DEFAULT_OPTIONS = {
	max: 3,
	backoffBaseMs: 1e3,
	backoffExponent: 1.5,
	onRetry: noop,
	shouldRetry: isNotAbort,
	signal: new AbortController().signal
};
async function privateRetry(operation, options, attemptNumber) {
	try {
		if (options.signal.aborted) throw new AbortError();
		const result = await operation(options.signal);
		if (options.signal.aborted) throw new AbortError();
		return result;
	} catch (e) {
		if (options.max <= 1) throw e;
		if (!shouldRetry(options.shouldRetry, e)) throw e;
		await waitMs(options.backoffBaseMs);
		options.onRetry(attemptNumber, e);
		return privateRetry(operation, {
			...options,
			max: options.max - 1,
			backoffBaseMs: options.backoffBaseMs * options.backoffExponent
		}, attemptNumber + 1);
	}
}
/** throws `AbortError` if the `AbortSignal` gets aborted */
function retry(operation, options = {}) {
	return privateRetry(operation, {
		...DEFAULT_OPTIONS,
		...options
	}, FIRST_ATTEMPT);
}
//#endregion
//#region src/backoff.ts
/**
* Calculates exponential backoff delay for retry operations.
*
* @param attempt The current attempt number
* @param baseMs The base delay in milliseconds (default: 1000ms)
* @param exponent The exponential multiplier (default: 2)
* @returns The calculated delay in milliseconds
*/
function calculateExponentialBackoff(attempt, baseMs = 1e3, exponent = 2) {
	return baseMs * exponent ** (attempt - 1);
}
/**
* Simple delay utility that returns a Promise that resolves after the specified duration.
*
* If an {@link AbortSignal} is provided, the delay resolves early when the signal
* aborts and the underlying timer is cleared so it does not dangle.
*
* @param ms Duration to wait in milliseconds
* @param signal Optional abort signal that resolves the delay early when aborted
* @returns Promise that resolves after the delay (or once aborted)
*/
function delay(ms, signal) {
	return new Promise((resolve) => {
		if (signal?.aborted) {
			resolve();
			return;
		}
		const timer = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve();
		}, ms);
		function onAbort() {
			clearTimeout(timer);
			resolve();
		}
		signal?.addEventListener("abort", onAbort, { once: true });
	});
}
//#endregion
//#region src/abort_signal_as_promise.ts
const getMessage = (abortSignalReason) => {
	if (abortSignalReason === void 0 || abortSignalReason === null) return "Operation was aborted";
	return abortSignalReason instanceof Error ? abortSignalReason.message : String(abortSignalReason);
};
/**
* Wraps an AbortSignal as a promise that can be raced against another async operation.
* This allows aborting any promise-based workflow that doesn't natively support AbortSignal
*/
function asPromise(signal) {
	return new Promise((_, reject) => {
		if (signal.aborted) {
			reject(new AbortError(getMessage(signal.reason)));
			return;
		}
		signal.addEventListener("abort", () => {
			reject(new AbortError(getMessage(signal.reason)));
		});
	});
}
//#endregion
export { AbortError, asPromise, calculateExponentialBackoff, delay, isAbortError, isNotAbort, retry };

//# sourceMappingURL=index.mjs.map